import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Controller, FieldValues, Path, UseFormReturn, FieldName, FieldErrors } from "react-hook-form";
import { DocumentStructure } from "../../../../models/document-structure";
import { ErrorMessage, FieldValuesFromFieldErrors } from "@hookform/error-message"
import { CssField } from "../../MuiInput.styled";
import { getValueByPath } from "../../../../helpers/helpers";
import { AccountStructure } from "../../../../models/accounts-structure";
import { getWorks } from "../../../../services/worksService";
import { Work } from "../../../../models/work";

interface WorkSearchProps<T extends FieldValues> {
    fieldStructure: AccountStructure | DocumentStructure, // field data
    useForm: UseFormReturn<T>; // form
    onSelect: (object: Work) => void, // update the form after object selected
    prefixPath?: string, // prefix path for the field in the form
    disabled?: boolean, // disable the field
}

function WorkSearch<T extends FieldValues>({ fieldStructure, useForm, onSelect, prefixPath, disabled}: WorkSearchProps<T>) {

    
    const formPath = (prefixPath? prefixPath + "." : "" ) + fieldStructure.field_name;

    const [options, setOptions] = useState<Work[]>([]);
    const [inputValue, setInputValue] = useState("");
    

    useEffect(() => {

        if (inputValue.length < 1) {
            setOptions([]);
            return;
        }

        let active = true;

        (async () => {
            try {

                const path = fieldStructure.field_name;
                const works = await searchWorks(path as Path<T>, inputValue);

                if (active) {
                    setOptions(works);
                }
            } catch (e) {
               console.error(e);
            }
        })();

        return () => {
            active = false;
        };

    }, [inputValue]);


    /**
     * search works by key and value
     * @param key - account key name 
     * @param value - value for search
     * @returns works
     */
    async function searchWorks(key: Path<T>, value: string): Promise<Work[]>{

        const body = {
            [key]: value
        }

        const works: Work[] = await getWorks('id', "5", 'ASC', ["customData","customer.customData"], body);

        return works;
        
    }


    /**
     * update the form with a new value
     * @param val - the new value, can be a specific field value or the whole object itself
     */
    function onChange(val: string | Work | null): void {

        if (typeof val === "string") {
            useForm.setValue(formPath as  Path<T>, val as any);
            return;
        }

        if (val === null) {
            useForm.setValue(formPath as  Path<T>, null as any); // or reset relevant fields
            return;
        }

        onSelect(val);
    }


    function renderOption(option: Work): string {

        if(fieldStructure.field_name === "work_code"){
            return String(getValueByPath(option, fieldStructure.field_name as keyof Work) + ": " + option["name" as keyof Work]);
        }

        return String(option["work_code" as keyof Work]+ ": " + String(getValueByPath(option, fieldStructure.field_name as keyof Work)));

    }


    function getValueByFieldName(work: Work): string {
        const path = fieldStructure.field_name;
        const value = getValueByPath(work, path)
        return String(value);
    }

    return (
        <CssField sx={{ minWidth: 200 }} error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}>
            <Controller
                name={formPath as Path<T>}
                control={useForm.control}
                render={({ field }) => (
                    <Autocomplete<Work, false, false, true>
                    {...field}
                    freeSolo
                    clearOnBlur={false}
                    options={options}
                    value={null}
                    getOptionLabel={(option) =>typeof option === "string" ? option : getValueByFieldName(option)}
                    inputValue={String(field.value ?? "")}
                    onChange={(_, newValue)=> onChange(newValue)}
                    disabled={disabled}

                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {renderOption(option)}
                        </li>
                    )}

                    onInputChange={(_, newInput) => {
                        field.onChange(newInput);
                        setInputValue(newInput);
                    }}

                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={fieldStructure.field_display_name}
                            error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}
                            helperText={<ErrorMessage errors={useForm.formState.errors} name={fieldStructure.field_name as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} />}
                        />
                    )}
                    />
                )}
            />
        </CssField>
        
    );
}

export default WorkSearch;